/**
 * Unit-numbering helpers for Setup Mode.
 *
 * "Lowest available" means the smallest positive integer (>= 1) that is not
 * already in use by an existing unit. Non-numeric or mixed labels (e.g.
 * "Penthouse", "2A", "B-12") are intentionally ignored — they cannot collide
 * with the integer suggestion. The strict check `String(n) === u.number.trim()`
 * filters out values like "01" or " 12 " that would otherwise parse as ints
 * but wouldn't roundtrip cleanly.
 *
 * Acceptance scenarios:
 *   []                       → "1"
 *   [1, 2, 3]                → "4"
 *   [1, 2, 4]                → "3"   (gap fills first)
 *   ["Penthouse"]            → "1"
 *   ["Penthouse", "1", "2"]  → "3"
 *
 * TODO(test-infra): vitest is not yet configured in /web (see web/package.json).
 * Once vitest infra is introduced, add `unit-numbering.test.ts` covering the
 * scenarios above plus `nextUnitAfter` (start-from-current+1, falls back to
 * lowest-available when current is non-numeric).
 */

/** Returns the lowest positive integer (as a string) not present in `units`. */
export function computeNextAvailableUnit(units: { number: string }[]): string {
  const numericInUse = collectNumericUnits(units);
  let candidate = 1;
  while (numericInUse.has(candidate)) candidate++;
  return String(candidate);
}

/**
 * Same algorithm as {@link computeNextAvailableUnit} but starts the search at
 * `parseInt(current) + 1`. Preserves the "+1 after submit" UX in Setup Mode
 * when the just-submitted unit was numeric. Falls back to lowest-available
 * when `current` is non-numeric or empty.
 */
export function nextUnitAfter(current: string, units: { number: string }[]): string {
  const trimmed = current.trim();
  const start = /^\d+$/.test(trimmed) ? parseInt(trimmed, 10) + 1 : 1;
  if (start <= 0) return computeNextAvailableUnit(units);
  const numericInUse = collectNumericUnits(units);
  let candidate = start;
  while (numericInUse.has(candidate)) candidate++;
  return String(candidate);
}

function collectNumericUnits(units: { number: string }[]): Set<number> {
  const result = new Set<number>();
  for (const u of units) {
    const trimmed = u.number.trim();
    const n = parseInt(trimmed, 10);
    // Strict: only count units whose entire trimmed string is a positive
    // integer. Skips "Penthouse", "2A", "B-12", "01", etc.
    if (Number.isInteger(n) && n > 0 && String(n) === trimmed) {
      result.add(n);
    }
  }
  return result;
}
